'use strict'

import * as BasUtil from '@basalte/bas-util'

angular
  .module('basalteApp')
  .factory('BasRoomLights', [
    '$rootScope',
    'BAS_ROOM',
    'BasSharedServerStorage',
    'BasUtilities',
    'CurrentBasCore',
    'RoomsHelper',
    'BasLight',
    basRoomLightsFactory
  ])

/**
 * @param $rootScope
 * @param {BAS_ROOM} BAS_ROOM
 * @param {BasSharedServerStorage} BasSharedServerStorage
 * @param {BasUtilities} BasUtilities
 * @param {CurrentBasCore} CurrentBasCore
 * @param {RoomsHelper} RoomsHelper
 * @param BasLight
 * @returns BasRoomLights
 */
function basRoomLightsFactory (
  $rootScope,
  BAS_ROOM,
  BasSharedServerStorage,
  BasUtilities,
  CurrentBasCore,
  RoomsHelper,
  BasLight
) {
  const K_GROUPS = 'groups'
  const K_LIGHTS = 'lights'

  const GROUP_UUID_PREFIX = 'group-'

  /**
   * @type {TBasSharedServerStorageState}
   */
  const basSharedServerStorage = BasSharedServerStorage.get()

  /**
   * @type {TCurrentBasCoreState}
   */
  const currentBasCoreState = CurrentBasCore.get()

  /**
   * @constructor
   * @param {BasRoom} basRoom
   */
  function BasRoomLights (basRoom) {

    /**
     * All BasLights
     *
     * @type {Object<string, BasLight>}
     */
    this.basLights = {}

    /**
     * User-defined group lights
     * Represents an array of group uuids
     *
     * @type {string[]}
     */
    this.uiUserLightGroups = []

    /**
     * Lights without a group
     * Represents an array of lightUuids that don't have a group
     *
     * @type {string[]}
     */
    this.uiLightsWithoutGroup = []

    /**
     * @type {boolean}
     */
    this.hasLightGroupOrder = false

    /**
     * @private
     * @type {BasRoom}
     */
    this._basRoom = basRoom

    this._basRoom.cssSetHasLights(
      RoomsHelper.roomHasFunctionLights(this._basRoom)
    )

    this.parseRoom()
  }

  /**
   * @param {BasRoom} room
   * @returns {boolean}
   */
  BasRoomLights.hasLights = function (room) {

    return (
      room &&
      room.room &&
      BasUtil.isNEArray(room.room.lights)
    )
  }

  /**
   * Parse the raw room from the parent BasRoom
   */
  BasRoomLights.prototype.parseRoom = function () {

    if (BasRoomLights.hasLights(this._basRoom)) {
      this.syncLights()
    }
  }

  BasRoomLights.prototype.syncLights = function () {

    this.resetLights()

    this.uiLightsWithoutGroup = []
    this.uiUserLightGroups = []
    this.hasLightGroupOrder = false

    if (BasRoomLights.hasLights(this._basRoom)) {

      const directLights = this._basRoom.room.lights

      // ==================================== //
      // Lights and server based light groups //
      // ==================================== //

      for (const lightUuid of directLights) {

        const device = CurrentBasCore.getDevice(lightUuid)

        if (device) {
          const light = new BasLight(device, this)
          this.basLights[lightUuid] = light

          const populateBasLightsWithConfigGroupLights = (lightToPopulate) => {
            if (Array.isArray(lightToPopulate.configGroupLightUuids)) {

              lightToPopulate.configGroupLightUuids.forEach(childUuid => {
                const childLightDevice = CurrentBasCore.getDevice(childUuid)

                if (childLightDevice) {
                  const subLight = new BasLight(childLightDevice, this)
                  this.basLights[childUuid] = subLight
                  populateBasLightsWithConfigGroupLights(subLight)
                }
              })
            }
          }
          populateBasLightsWithConfigGroupLights(light)
        }
      }

      // ================================================== //
      // App light groups (shared server storage / appdata) //
      // ================================================== //

      const lightGroupOrder = basSharedServerStorage.lightGroupOrder
      const remainingRoomLightKeys = [...directLights]

      this.hasLightGroupOrder = !!lightGroupOrder

      // Remove old group keys
      this.basLights = Object.fromEntries(
        Object.entries(this.basLights)
          .filter(([key]) => !key.includes(GROUP_UUID_PREFIX))
      )

      const roomLightGroupOrder = lightGroupOrder[this._basRoom.id]
      const lightGroups = roomLightGroupOrder?.groups
      const lightsWithoutGroup = roomLightGroupOrder?.lights

      if (lightGroups && lightsWithoutGroup) {

        const removeFromKeysAndAddToArray = (lightUuid, lightsArray) => {
          const idx = remainingRoomLightKeys.indexOf(lightUuid)
          if (idx >= 0) {
            lightsArray.push(lightUuid)
            remainingRoomLightKeys.splice(idx, 1)
          }
        }

        // Adjust groups to not include lights that are not direct children
        const adjustedLightGroups = lightGroups
          .map(group => ({
            ...group,
            lights: [...group.lights]
              .filter(uuid => this._basRoom.room.lights.includes(uuid))
          }))

        // Filter out groups with less than 2 lights
        const filteredLightGroups = adjustedLightGroups
          .filter(group => group.lights.length >= 2)
        for (const lightGroup of filteredLightGroups) {

          const lightUuids = []
          for (const lightUuid of lightGroup.lights) {
            removeFromKeysAndAddToArray(lightUuid, lightUuids)
          }

          this.uiUserLightGroups.push(lightGroup.uuid)
          this.basLights[lightGroup.uuid] = new BasLight(
            {
              name: lightGroup.name,
              uuid: lightGroup.uuid,
              userGroupLightUuids: lightUuids
            },
            this
          )
          this.updateGroupToggleStateWithWhiteSupport(lightGroup.uuid)
        }

        // Lights without group (order matters)
        for (const lightUuid of lightsWithoutGroup) {
          removeFromKeysAndAddToArray(lightUuid, this.uiLightsWithoutGroup)
        }
      }

      // ================ //
      // Remaining lights //
      // ================ //

      // Lights not represented by light groups (appdata) or light without group
      //  -> add to lights without group. This also includes server based light
      //  groups, as they are just lights to us.
      for (const key of remainingRoomLightKeys) {

        if (!key.startsWith(GROUP_UUID_PREFIX)) {

          const light = this.basLights[key]

          if (light) this.uiLightsWithoutGroup.push(light.uuid)
        }
      }
    }

    this.checkLightChange()
  }

  /**
   * Checks if this BasRoomLights instance has any lights
   *
   * @returns {boolean}
   */
  BasRoomLights.prototype.hasLights = function () {

    return Object.keys(this.basLights).length > 0
  }

  /**
   * Checks whether a light in the room is active
   *
   * @returns {boolean}
   */
  BasRoomLights.prototype.areLightsActive = function () {
    const keys = Object.keys(this.basLights)
    const length = keys.length

    if (length > 0) {

      for (const key of keys) {

        const light = this.basLights[key]

        if (light?.onState === true) return true
      }
    }

    return false
  }

  /**
   * Checks whether a light in the room is active
   */
  BasRoomLights.prototype.checkLightChange = function () {

    if (this._basRoom) {

      this._basRoom.cssSetActiveLights(this.areLightsActive())
      $rootScope.$emit(BAS_ROOM.EVT_ACTIVITY_CHANGED, this._basRoom.id)
      $rootScope.$emit(BAS_ROOM.EVT_ACTIVITY_LIGHTS, this._basRoom.id)
    }
  }

  /**
   * Gets a BasLight for the uuid
   *
   * @param {string} uuid
   * @returns {?BasLight}
   */
  BasRoomLights.prototype.getLight = function (uuid) {

    return this.basLights[uuid]
  }

  BasRoomLights.prototype.turnOffAllLights = function () {

    Object.values(this.basLights)
      .filter(light => light?.onState)
      .forEach(light => light.toggle())
  }

  /**
   * Creates new BasLight instances from current real BasLight instances
   *
   * @returns {BasLight[]}
   */
  BasRoomLights.prototype.createEditableClones = function () {

    const result = []
    const keys = Object.keys(this.basLights)
    const length = keys.length

    if (length > 0) {

      for (const key of keys) {

        const light = this.basLights[key]

        if (light?.isEditable()) {

          const clone = light.clone()
          clone.fillInUuid()

          result.push(clone)
        }
      }
    }

    return result
  }

  /**
   * Clears all lights
   */
  BasRoomLights.prototype.clearLights = function () {

    Object.values(this.basLights).forEach(light => light.clear())
  }

  BasRoomLights.prototype.suspendLights = function () {

    Object.values(this.basLights).forEach(light => light.suspend())
  }

  /**
   * Resets the lights, removing them
   */
  BasRoomLights.prototype.resetLights = function () {

    this.clearLights()

    this.lights = []
  }

  BasRoomLights.prototype.suspend = function () {

    this.suspendLights()
  }

  BasRoomLights.prototype.destroy = function () {

    this.resetLights()
  }

  BasRoomLights.prototype.updateTranslation = function () {

    Object.values(this.basLights).forEach(light => light.updateTranslation())
  }

  BasRoomLights.prototype.createGroup = function () {

    const uiLightGroupIndex = this.uiUserLightGroups.length + 1
    const groupName =
      BasUtilities.translate('group') + ' ' + uiLightGroupIndex

    const groupUuid = GROUP_UUID_PREFIX + BasUtil.uuidv4()

    this.basLights[groupUuid] = new BasLight(
      {
        name: groupName,
        uuid: groupUuid,
        userGroupLightUuids: []
      },
      this
    )

    this.uiUserLightGroups.push(groupUuid)
  }

  BasRoomLights.prototype.removeGroup = function (uiLightGroupUuid) {

    this.basLights[uiLightGroupUuid].userGroupLightUuids = []

    const index = this.uiUserLightGroups.indexOf(uiLightGroupUuid)

    if (index >= 0) {

      this.uiUserLightGroups.splice(index, 1)
      delete this.basLights[uiLightGroupUuid]
    }
  }

  BasRoomLights.prototype.updateGroupToggleStateWithWhiteSupport =
    function (uiLightGroupUuid) {

      const virtualBasLight = this.basLights[uiLightGroupUuid]

      if (virtualBasLight instanceof BasLight) {

        virtualBasLight.onState = false

        for (const lightUuid of virtualBasLight.userGroupLightUuids) {

          const light = this.basLights[lightUuid]

          if (light && light.canToggleOn && light.onState) {

            virtualBasLight.onState = true

            if (light._supportsWhiteViaBrightness) {

              virtualBasLight._supportsWhiteViaBrightness = true
            }
          }

          if (!virtualBasLight.onState) virtualBasLight.syncModeUI()
        }

        this.checkLightChange()
      }
    }

  BasRoomLights.prototype.syncGroupBrightnessSlider = function () {

    for (const uiLightGroupUuid of this.uiUserLightGroups) {

      const virtualBasLight = this.basLights[uiLightGroupUuid]

      if (virtualBasLight?.canChangeBrightness) {

        virtualBasLight.updateGroupBrightness()
      }
    }
  }

  BasRoomLights.prototype.updateServerStorageWithGroups = function () {
    const obj = {}

    obj[K_GROUPS] = []
    obj[K_LIGHTS] = []

    for (const uiLightGroupUuid of this.uiUserLightGroups) {

      const virtualBasLight = this.basLights[uiLightGroupUuid]

      if (virtualBasLight.userGroupLightUuids) {

        obj[K_GROUPS].push(
          {
            name: virtualBasLight.name,
            lights: virtualBasLight.userGroupLightUuids,
            uuid: uiLightGroupUuid
          }
        )
      }
    }

    for (const lightUuid of this.uiLightsWithoutGroup) {
      if (lightUuid) obj[K_LIGHTS].push(lightUuid)
    }

    currentBasCoreState.core.core.sharedServerStorage
      .updateLightGroupOrder(this._basRoom.id, obj)
  }

  /**
   * Get group of lights
   *
   * @param {string} uuid
   * @returns {?BasLight}
   */
  BasRoomLights.prototype.getGroupByUuid = function (uuid) {

    if (this.uiUserLightGroups.includes(uuid)) return this.basLights[uuid]

    return null
  }

  return BasRoomLights
}
